import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from "../ui/button";
import { X, User, Calendar as CalendarIcon, Clock, Mail, Phone, AlertTriangle, Pencil } from 'lucide-react';
import { getStatusColor } from '../../types/calendar-types';
import InputWithIcon from '../generics/input-with-icon';
import { formatDateInTimeZone, formatTimeInTimeZone } from '../../utils/timezone';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useCalendarViewModel} from '../../view-model/professional/calendar-view-model';
import {calendarStore} from "../../store/calendar-store";


const ReservationCalendarDetails: React.FC = observer(() => {

  const calendarViewModel = useCalendarViewModel()
  const { t } = useTranslation();
  
  if (!calendarViewModel.getShowEventDetails || !calendarStore.selectedBooking) return null;

  // Après cette ligne, selectedBooking est garanti non-null
  const selectedBooking = calendarStore.selectedBooking;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto animate-in fade-in zoom-in-95 duration-200"
      >
        {/* En-tête du modal */}
        <div className="relative border-b border-gray-100 dark:border-gray-800 p-5">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div
                className="w-3 h-10 rounded-sm"
                style={{ backgroundColor: getStatusColor(selectedBooking.status) }}
              ></div>
              <h3 className="text-xl font-semibold">
                {calendarViewModel.getIsCancelling ? t('calendar.cancelAppointment', 'Annuler le rendez-vous') : t('calendar.appointmentDetails', 'Détails du rendez-vous')}
              </h3>
            </div>
            <button
              onClick={() => calendarViewModel.closeEventDetails()}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X size={18} className="text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {calendarViewModel.getIsCancelling ? (
            <div className="space-y-6">
              <div className="flex items-start gap-3 mb-6">
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
                  <AlertTriangle className="text-red-500 dark:text-red-400" size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-red-600 dark:text-red-400">{t('calendar.cancelAppointment', 'Annuler le rendez-vous')}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t('calendar.cancelAppointmentDescription', 'Cette action est irréversible. Le client sera notifié de l\'annulation.')}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-100 dark:border-gray-800">
                <Button
                  onClick={() => calendarViewModel.setIsCancelling()}
                  variant="outline"
                  className="gap-2 rounded-lg"
                >
                  {t('calendar.return', 'Retour')}
                </Button>
                <Button
                  className="gap-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                  onClick={() => calendarViewModel.handleStatusChange(selectedBooking.id)}
                >
                  {t('calendar.confirmCancellation', 'Confirmer l\'annulation')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-lg" style={{ backgroundColor: `${getStatusColor(selectedBooking.status)}20` }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: getStatusColor(selectedBooking.status) }}>
                    <CalendarIcon className="text-white" size={16} />
                  </div>
                </div>
                <div>
                  {/* Display services - support both multi-services and legacy single service */}
                  {selectedBooking.services && selectedBooking.services.length > 0 ? (
                    // Multi-services display
                    <div className="space-y-2">
                      {selectedBooking.services.map((service: any, index: number) => (
                        <div key={index} className="flex items-center gap-2">
                          <h4 className="text-lg font-semibold">
                            {service.serviceName}
                          </h4>
                          {service.options && service.options.length > 0 && (
                            <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                              {t('calendar.options', 'Options')}: {service.options.map((opt: any) => opt.name).join(', ')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Legacy single service display
                    <div className="flex items-center gap-2">
                      <h4 className="text-lg font-semibold">
                        {selectedBooking.service ? selectedBooking.service.name :
                            selectedBooking.serviceName ? selectedBooking.serviceName : 'Service non spécifié'}
                      </h4>
                      {selectedBooking.options && selectedBooking.options.length > 0 && (
                        <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                          {t('calendar.options', 'Options')}: {selectedBooking.options.map(opt => opt.name).join(', ')}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${selectedBooking.status === 'confirmed' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' : selectedBooking.status === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : selectedBooking.status === 'completed' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'}`}>
                      {selectedBooking.status === 'confirmed' ? t('calendar.confirmed', 'Confirmé') :
                          selectedBooking.status === 'cancelled' ? t('calendar.cancelled', 'Annulé') :
                              selectedBooking.status === 'completed' ?  t('calendar.terminer', 'Terminé') : ""}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 mt-2">
                    {selectedBooking.status === 'cancelled' ? (
                      // ========== RÉSERVATIONS ANNULÉES ==========
                      <div className="text-sm space-y-1">
                        {selectedBooking.paidAmount !== undefined && selectedBooking.paidAmount > 0 ? (
                          // Il y avait un paiement
                          <>
                            <div className="text-red-600 dark:text-red-400 font-medium">
                              ⚠️ {t('calendar.refundClient', 'Remboursement client')}
                            </div>
                            <div className="text-gray-700 dark:text-gray-300">
                              {t('calendar.refundedAmount', 'Montant remboursé au client')}: {selectedBooking.refundAmount ? Number(selectedBooking.refundAmount).toFixed(2) : '0.00'} €
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 italic">
                              {t('calendar.refundDeductedFromPayments', 'Le remboursement sera automatiquement déduit de vos prochains virements')}
                            </div>
                          </>
                        ) : (
                          // Pas de paiement en ligne
                          <div className="text-green-600 dark:text-green-400 font-medium">
                            ✓ {t('calendar.noRefund', 'Pas de remboursement')}
                          </div>
                        )}
                      </div>
                    ) : (
                      // ========== RÉSERVATIONS CONFIRMÉES/COMPLÉTÉES ==========
                      <div className="text-sm space-y-1">
                        {selectedBooking.remainingBalance && Number(selectedBooking.remainingBalance) > 0 ? (
                          // SERVICE AVEC ACOMPTE (basé sur remainingBalance)
                          <>
                            <div className="text-gray-700 dark:text-gray-300">
                              {t('calendar.totalServicePrice', 'Prix total du service')}: {Number(selectedBooking.totalPrice).toFixed(2)} €
                            </div>
                            <div className="text-green-600 dark:text-green-400 font-semibold">
                              {t('calendar.netAmountReceivedDeposit', 'Montant net reçu (acompte)')}: {selectedBooking.netAmount ? Number(selectedBooking.netAmount).toFixed(2) : (selectedBooking.paidAmount ? Number(selectedBooking.paidAmount).toFixed(2) : '0.00')} €
                            </div>
                            <div className="text-red-600 dark:text-red-400 font-semibold">
                              {t('calendar.remainingToCollect', 'Solde à encaisser')}: {Number(selectedBooking.remainingBalance).toFixed(2)} €
                            </div>
                          </>
                        ) : selectedBooking.paidAmount !== undefined && Number(selectedBooking.paidAmount) > 0 ? (
                          // PAIEMENT INTÉGRAL
                          <>
                            <div className="text-green-600 dark:text-green-400 font-semibold">
                              {t('calendar.netAmountReceived', 'Montant net reçu')}: {selectedBooking.netAmount ? Number(selectedBooking.netAmount).toFixed(2) : (selectedBooking.paidAmount ? Number(selectedBooking.paidAmount).toFixed(2) : '0.00')} €
                            </div>
                          </>
                        ) : (
                          // PAS DE PAIEMENT EN LIGNE (abonnement)
                          <>
                            <div className="text-gray-700 dark:text-gray-300 font-medium">
                              {t('calendar.totalPrice', 'Prix total')}: {Number(selectedBooking.totalPrice).toFixed(2)} €
                            </div>
                            <div className="text-red-600 dark:text-red-400 font-semibold">
                              {t('calendar.statusToCollect', 'Statut du paiement')}: {t('calendar.toCollect', 'À encaisser')}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-5 space-y-4">
                {/* Informations client */}
                <div>
                  <InputWithIcon
                    label={t('calendar.clientName', 'Nom du client')}
                    id="clientName"
                    name="clientName"
                    value={selectedBooking.client ? `${selectedBooking.client.firstName} ${selectedBooking.client.lastName}` :
                        selectedBooking.clientFirstName && selectedBooking.clientLastName ?
                           `${selectedBooking.clientFirstName} ${selectedBooking.clientLastName}` :
                            selectedBooking.clientName ? selectedBooking.clientName : 'None'}
                    icon={<User size={16} />}
                    disabled
                  />
                </div>

                <InputWithIcon
                    label={t('calendar.clientEmail', 'Email')}
                    id="clientEmail"
                    name="clientEmail"
                    value={selectedBooking.client?.email || selectedBooking.clientEmail || 'None'}
                    icon={<Mail size={16} />}
                    disabled
                  />

                <InputWithIcon
                  label={t('calendar.clientPhone', 'Téléphone')}
                  id="clientPhone"
                  name="clientPhone"
                  value={selectedBooking.client?.phone || selectedBooking.clientPhone || 'None'}
                  icon={<Phone size={16} />}
                  disabled
                />

                {/* Date et heure */}
                <div className="grid grid-cols-2 gap-4">
                  <InputWithIcon
                    label={t('calendar.date', 'Date')}
                    id="date"
                    name="date"
                    value={(() => {
                      try {
                        // Utiliser formatDateInTimeZone pour formater la date dans le fuseau horaire du professionnel
                        return formatDateInTimeZone(
                            selectedBooking.startTime,
                          'EEEE d MMMM',
                          { locale: fr }
                        );
                      } catch (error) {
                        console.error('Erreur de formatage de date:', error);
                        return 'Date non disponible';
                      }
                    })()}
                    icon={<CalendarIcon size={16} />}
                    disabled
                  />

                  <InputWithIcon
                    label={t('calendar.time', 'Heure')}
                    id="time"
                    name="time"
                    value={(() => {
                      try {
                        // Utiliser formatTimeInTimeZone pour formater les heures dans le fuseau horaire du professionnel
                        const startTime = formatTimeInTimeZone(selectedBooking.startTime, 'HH:mm');
                        const endTime = formatTimeInTimeZone(selectedBooking.endTime, 'HH:mm');
                        return `${startTime} - ${endTime}`;
                      } catch (error) {
                        console.error('Erreur de formatage d\'heure:', error);
                        return 'Heure non disponible';
                      }
                    })()}
                    icon={<Clock size={16} />}
                    disabled
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-100 dark:border-gray-800">
                <Button
                  onClick={() => calendarViewModel.closeEventDetails()}
                  variant="outline"
                  className="gap-2 rounded-lg"
                >
                  {t('calendar.close', 'Fermer')}
                </Button>
                {selectedBooking.isManualBooking && selectedBooking.status !== 'cancelled' && selectedBooking.status !== 'completed' && (
                  <Button
                    onClick={() => {
                      calendarViewModel.openEditManualBookingModal(selectedBooking);
                      calendarViewModel.closeEventDetails();
                    }}
                    className="gap-2 bg-primary hover:bg-primary/90 text-white rounded-lg"
                  >
                    <Pencil size={16} />
                    {t('calendar.editBooking', 'Modifier')}
                  </Button>
                )}
                {selectedBooking.status !== 'cancelled' && (
                  <Button
                    onClick={() => calendarViewModel.setIsCancelling()}
                    className="gap-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                  >
                    <AlertTriangle size={16} />
                    {t('calendar.cancelAppointment', 'Annuler le rendez-vous')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default ReservationCalendarDetails;
